import type { Landmark, PaletteModel } from '../../../data/types';
import type { Rng } from '../../../core/rng';
import { centroid, pointInRing } from '../../../geo/polygon';
import { DAM_DECK } from '../../../site/damDeck';
import type { ChunkSink } from '../../mesh/chunker';
import type { Obstacle } from '../../types';
import type { NearDetailSet } from '../../mesh/nearDetailLod';
import { elevatedBuilder, elevatedDetails, elevatedTracery } from '../../mesh/elevatedBuilder';
import { footedBuilder } from '../../mesh/footedBuilder';
import { foundationHeight, footDepth } from '../foundation';
import { createTraceryCollector } from './tracery/tracerySink';
import type { TraceryKind } from './tracery/traceryLayout';
import { buildLandmark, type LandmarkContext } from './landmarkFeature';
import { landmarkFooting } from './landmarkFooting';

/** Every landmark into the fabric chunks, and the churches' window tracery for the near tier (tracery/traceryLod.ts). */
export function buildAllLandmarks(
  landmarks: readonly Landmark[],
  chunks: ChunkSink,
  palette: PaletteModel,
  rng: Rng,
  context: LandmarkContext
): {
  triangles: number;
  obstacles: readonly Obstacle[];
  tracery: readonly NearDetailSet<TraceryKind>[];
} {
  const obstacles: Obstacle[] = [];
  const tracery = createTraceryCollector();
  const dams = landmarks.filter((landmark) => landmark.kind === 'dam');
  let triangles = 0;
  landmarks.forEach((landmark, i) => {
    const height = context.elevationAt
      ? foundationHeight(landmark.footprint, context.elevationAt)
      : (context.elevation ?? 0);
    // The Dam and its market already use absolute elevations on the shared crest.
    const absolute =
      landmark.kind === 'dam' ||
      typeof landmark.params['baseY'] === 'number' ||
      landmark.id.startsWith('landmark-blokhuis-boom-');
    const raw = chunks.builderAt('fabric', centroid(landmark.footprint));
    const depth =
      !absolute && context.elevationAt
        ? footDepth(landmarkFooting(landmark), context.elevationAt, height)
        : 0;
    const builder = elevatedBuilder(footedBuilder(raw, height, depth), absolute ? 0 : height);
    const result = buildLandmark(landmark, builder, palette, rng.fork(`landmark:${landmark.id}`), {
      ...context,
      marketOnDam:
        landmark.kind === 'marketStalls' &&
        landmark.params['baseY'] === DAM_DECK.crestY &&
        dams.some((dam) => landmark.footprint.every((p) => pointInRing(p, dam.footprint))),
      elevation: absolute ? 0 : height,
      details: elevatedDetails(context.details, absolute ? 0 : height),
      edgeIdBase: i,
      tracery: elevatedTracery(tracery, absolute ? 0 : height),
    });
    triangles += result.triangles;
    obstacles.push(...result.obstacles);
  });
  return { triangles, obstacles, tracery: tracery.finish() };
}
